// Pixar Singing Ad's lyrics as the form edits them (spec G14-pixar-singing-ad, "The lyrics
// editor"; live's `director/lyrics.ts`, `song/length-plan.ts`, `schema.ts` and the shared
// `utils/syllables.ts`, read as a behaviour spec): the six parts and their caps, the syllables and
// the song's length recounted on every edit for live's meta line, live's gate before Approve
// lyrics, and the blank lyrics the pencil writes. The server recounts and checks them again at
// approval (`templates/pixar_singing_lyrics.py`); this is what the member sees while typing.
import type { PixarLyrics } from "../../../api/generated/model";

/** The six sung parts in order, each with its beat label and its syllable cap; the turn's words
 * are fixed, so it has none (live's `LYRIC_PARTS`, `LYRIC_PART_LABELS`). */
export const LYRIC_PARTS: readonly { part: string; beat: string; cap: number | null }[] = [
  { part: "setup", beat: "setup", cap: 9 },
  { part: "turn", beat: "turn", cap: null },
  { part: "couplet_a1", beat: "couplet", cap: 9 },
  { part: "couplet_a2", beat: "couplet", cap: 9 },
  { part: "couplet_b1", beat: "couplet", cap: 9 },
  { part: "brand", beat: "brand", cap: 10 },
];
export const TURN_INDEX = 1;
export const TURN_TEXT = "said no one ever";
export const SYLLABLE_BUDGET = 46;
export const PUNCH_LINE_MAX_SYLLABLES = 8;
export const PUNCH_LINE_BEAT = "punch line";

/** Live's words for what keeps the lyrics from approval (`constants.ts:119-125`). */
export const GENERATE_LYRICS = "Generate or write the lyrics";
export const LYRICS_SHAPE = "The lyrics need their 6 lines in order — generate them again";
export const EMPTY_LINE = "Every line needs its text";
export const EMPTY_PUNCH_LINE = "Write the punch line";
export const EMPTY_BUTTON = "Fill in the CTA: it is the end card button";

const WORD = /[a-z']+/g;
const VOWEL_RUN = /[aeiouy]+/g;
// A final "e", then a final "ed", is silent unless the word ends in one of these.
const SILENT_ENDINGS: readonly { ending: string; sounded: readonly string[] }[] = [
  { ending: "e", sounded: ["le", "ee", "ye"] },
  { ending: "ed", sounded: ["ted", "ded"] },
];

/** One lower-case word's syllables: its vowel runs (`y` a vowel), less a silent ending each,
 * never under one. */
function wordSyllables(word: string): number {
  const letters = word.replaceAll("'", "");
  let count = letters.match(VOWEL_RUN)?.length ?? 0;
  for (const silent of SILENT_ENDINGS) {
    const sounded = silent.sounded.some((ending) => letters.endsWith(ending));
    if (count > 1 && letters.endsWith(silent.ending) && !sounded) count -= 1;
  }
  return Math.max(count, 1);
}

/** Live's `countSyllables`: every `[a-z']+` word of the lower-cased text. */
export function countSyllables(text: string): number {
  return (text.toLowerCase().match(WORD) ?? []).reduce((total, word) => total + wordSyllables(word), 0);
}

// Live's length plan (`length-plan.ts:6-18`): the lead, the singing and speaking rates, the gaps,
// the ring-out and the end card's least, the song's bounds.
const LEAD_SECONDS = 0.3;
const SUNG_PER_SECOND = 3.9;
const TURN_PER_SECOND = 5;
const GAP_BEFORE: Readonly<Record<number, number>> = { 1: 0, 2: 2.4, 3: 1.1 };
const LINE_GAP_SECONDS = 0.35;
const PUNCH_LINE_GAP_SECONDS = 0.4;
const PUNCH_LINE_PER_SECOND = 3;
const RING_OUT_SECONDS = 2;
const END_CARD_MIN_SECONDS = 4;
const MIN_SONG_SECONDS = 16;
const MAX_SONG_SECONDS = 25;

/** A tenth of a second as live's JavaScript rounds it. */
function tenth(seconds: number): number {
  return Math.round(seconds * 10) / 10;
}

/** Live's `toSongSeconds`: the song's whole seconds planned from its lines (by id: line 2 is the
 * turn) and its punch line, 16 to 25. */
export function songSeconds(lyrics: Pick<PixarLyrics, "lines" | "tag">): number {
  let clock = LEAD_SECONDS;
  for (const line of lyrics.lines) {
    const begins = clock + (GAP_BEFORE[line.id] ?? LINE_GAP_SECONDS);
    clock = begins + countSyllables(line.text) / (line.id === 2 ? TURN_PER_SECOND : SUNG_PER_SECOND);
  }
  const spoken = tenth(tenth(clock) + PUNCH_LINE_GAP_SECONDS);
  const spokenEnd = tenth(spoken + countSyllables(lyrics.tag.trim()) / PUNCH_LINE_PER_SECOND);
  const seconds = Math.max(Math.ceil(spokenEnd + RING_OUT_SECONDS), Math.floor(spoken) + END_CARD_MIN_SECONDS);
  return Math.min(Math.max(seconds, MIN_SONG_SECONDS), MAX_SONG_SECONDS);
}

/** Live's meta line, recounted as the lines are edited (`toLyricsMeta`). */
export function lyricsMeta(lyrics: Pick<PixarLyrics, "lines" | "tag">): string {
  const syllables = lyrics.lines.reduce((total, line) => total + countSyllables(line.text), 0);
  return `6 lines + punch line · ${syllables}/${SYLLABLE_BUDGET} syllables · ≈ ${songSeconds(lyrics)} s`;
}

/** Live's `findLyricsIssue`: the first thing keeping the lyrics from approval, or null. The CTA
 * chip is the end card's button where it is filled. */
export function lyricsIssue(lyrics: PixarLyrics | null, cta: string): string | null {
  if (lyrics === null) return GENERATE_LYRICS;
  if (lyrics.lines.length !== LYRIC_PARTS.length) return LYRICS_SHAPE;
  if (lyrics.lines.some((line) => !line.text.trim())) return EMPTY_LINE;
  if (!lyrics.tag.trim()) return EMPTY_PUNCH_LINE;
  if (!cta.trim() && !lyrics.end_card.cta_button.trim()) return EMPTY_BUTTON;
  return null;
}

/** Live's `toBlankLyrics`: the six lines to write by hand, the turn already in place. */
export function blankLyrics(): PixarLyrics {
  return {
    problem: "",
    pain_moment: "",
    product_short_name: "",
    claims: [],
    lines: LYRIC_PARTS.map((each, index) => ({
      id: index + 1,
      part: each.part,
      text: index === TURN_INDEX ? TURN_TEXT : "",
      delivery: "",
      on_screen: "",
      claim_ids: [],
    })),
    tag: "",
    end_card: { cta_button: "", offer_line: "" },
    pronunciations: [],
    word_count: 0,
    song_seconds: 0,
  };
}

/** Whether two lyrics read the same: their lines' texts and their punch line (live's
 * `hasSameLineTexts` and the tag). */
export function sameLyrics(one: PixarLyrics | null, other: PixarLyrics | null): boolean {
  if (one === null || other === null) return one === other;
  return (
    one.tag === other.tag &&
    one.lines.length === other.lines.length &&
    one.lines.every((line, index) => line.text === other.lines[index]?.text)
  );
}
